/**
 * 클라이언트 측 엑셀(CSV) 내보내기 유틸리티
 * Excel에서 한글이 깨지지 않도록 UTF-8 BOM을 추가
 */

export interface ExcelColumn<T> {
    header: string;
    key: keyof T;
    width?: number;
}

/**
 * 데이터를 CSV 형식의 엑셀 파일로 다운로드
 * @param data 내보낼 데이터 배열
 * @param columns 컬럼 정의 (헤더명과 데이터 키 매핑)
 * @param filename 다운로드할 파일명 (확장자 제외)
 */
export const exportToExcel = <T extends object>(
    data: T[],
    columns: ExcelColumn<T>[],
    filename: string
): void => {
    // CSV 헤더 생성
    const headers = columns.map(col => `"${col.header}"`).join(',');

    // CSV 데이터 행 생성
    const rows = data.map(item => {
        return columns.map(col => {
            const value = item[col.key] as unknown;
            // null, undefined 처리
            if (value === null || value === undefined) {
                return '""';
            }
            // 문자열 값에서 쌍따옴표 이스케이프 처리
            const stringValue = String(value).replace(/"/g, '""');
            return `"${stringValue}"`;
        }).join(',');
    });

    // CSV 콘텐츠 생성 (UTF-8 BOM 추가)
    const BOM = '\uFEFF';
    const csvContent = BOM + [headers, ...rows].join('\r\n');

    // Blob 생성 및 다운로드
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // URL 해제
    URL.revokeObjectURL(url);
};

/**
 * 현재 날짜/시간을 파일명에 사용할 수 있는 형식으로 반환
 * @returns YYYYMMDD_HHMMSS 형식의 문자열
 */
export const getTimestampForFilename = (): string => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    return `${year}${month}${day}_${hours}${minutes}${seconds}`;
};
